import { FC, useEffect, useState, useCallback } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { Tabs } from 'antd';
import { Loader } from 'components/Global';
import { checkPermission } from 'utils/utils';
import { AppDispatch } from 'redux/store';
import { getEmployeesListAction } from 'redux/action/employeesTableAction';
import EmployeesTable from './Table';
import NameMappingsTable from './NameMappings';
import styles from './index.module.scss';

const Employees: FC = () => {

    const dispatch = useDispatch<AppDispatch>();

    const selectedCompanyPermission = useSelector(
        (state: any) => state?.companies?.selectedCompanyDetails?.role?.permissions
    );

    const { selectedCompanyDetails } = useSelector(
        (state: any) => state?.companies
    );

    const {
        data: employeesData = [],
        total = 0,
        currentPage = 1,
        isLoading = false,
        fistTimeFetchLoading = true,
    } = useSelector((state: any) => state.employeesTable || {});

    const [activeTab, setActiveTab] = useState('employees');
    const [searchValue, setSearchValue] = useState('');
    const [debouncedSearchValue, setDebouncedSearchValue] = useState('');
    const [filterValue, setFilterValue] = useState('all');
    const [sortBy, setSortBy] = useState('fullName');
    const [sortOrder, setSortOrder] = useState('asc');
    const [isInViewPort, setIsInViewPort] = useState(false);
    const [isInitialMount, setIsInitialMount] = useState(true);

    const isViewPermission = checkPermission(selectedCompanyPermission, {
        permissionName: 'Employees',
        permission: ['view'],
    });

    // Debounce search input
    useEffect(() => {
        if (isInitialMount) {
            return;
        }
        const timer = setTimeout(() => {
            setDebouncedSearchValue(searchValue);
        }, 500);

        return () => clearTimeout(timer);
    }, [searchValue, isInitialMount]);

    // Fetch employees list
    const fetchEmployees = useCallback(
        (page: any = 1) => {
            dispatch(
                getEmployeesListAction({
                    companyId: localStorage.getItem('companyId') || '',
                    status: filterValue,
                    search: debouncedSearchValue || undefined,
                    sortBy: sortBy || undefined,
                    sortOrder: sortOrder || undefined,
                    page: page,
                    limit: 20,
                })
            );
        },
        [dispatch, filterValue, debouncedSearchValue, sortBy, sortOrder]
    );

    // Initial load and when company changes
    useEffect(() => {
        if (selectedCompanyDetails && isViewPermission) {
            setSearchValue('');
            setDebouncedSearchValue('');
            setFilterValue('all');
            setSortBy('fullName');
            setSortOrder('asc');
            setIsInitialMount(true);
            fetchEmployees(1);
            // Set isInitialMount to false after initial load
            setTimeout(() => {
                setIsInitialMount(false);
            }, 100);
        }
    }, [selectedCompanyDetails, isViewPermission]);

    // When debounced search value changes
    useEffect(() => {
        if (selectedCompanyDetails && isViewPermission && !isInitialMount) {
            setIsInViewPort(false);
            fetchEmployees(1);
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [debouncedSearchValue]);

    // When filter changes
    useEffect(() => {
        if (selectedCompanyDetails && isViewPermission && !isInitialMount) {
            setIsInViewPort(false);
            fetchEmployees(1);
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [filterValue]);

    // When sort changes
    useEffect(() => {
        if (selectedCompanyDetails && isViewPermission && !isInitialMount) {
            setIsInViewPort(false);
            fetchEmployees(1);
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [sortBy, sortOrder]);

    // Infinite scroll - load more when in viewport
    useEffect(() => {
        if (
            isInViewPort &&
            employeesData &&
            Array.isArray(employeesData) &&
            employeesData.length < total &&
            !isLoading &&
            selectedCompanyDetails &&
            isViewPermission
        ) {
            fetchEmployees(currentPage + 1);
            setIsInViewPort(false); // Reset to prevent multiple calls
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [isInViewPort]);

    // Search handler
    const performSearchHandler = (
        event: React.ChangeEvent<HTMLInputElement>
    ) => {
        setSearchValue(event.target.value.trimStart());
    };

    // Filter handler
    const performFilterHandler = (value: string) => {
        setFilterValue(value);
    };

    // Sort handler
    const performSortHandler = (column: string) => {
        const newOrder =
            sortBy === column && sortOrder === 'asc' ? 'desc' : 'asc';
        setSortBy(column);
        setSortOrder(newOrder);
    };

    if (!isViewPermission) {
        return null;
    }

    // Tab 1: the existing employees table (unchanged logic). Tab 2 (WS-14): the
    // Name Mappings table. Inactive tabs are lazy by default, so NameMappingsTable
    // only mounts/fetches when the user opens it.
    const employeesTab = (
        <div>
            {fistTimeFetchLoading ? (
                <Loader />
            ) : (
                <EmployeesTable
                    performSearchHandler={performSearchHandler}
                    searchValue={searchValue}
                    performFilterHandler={performFilterHandler}
                    filterValue={filterValue}
                    performSortHandler={performSortHandler}
                    sortBy={sortBy}
                    sortOrder={sortOrder}
                    setIsInViewPort={setIsInViewPort}
                />
            )}
        </div>
    );

    return (
        <div className={styles['employees']}>
            <Tabs
                activeKey={activeTab}
                onChange={setActiveTab}
                items={[
                    {
                        key: 'employees',
                        label: 'Employees',
                        children: employeesTab,
                    },
                    {
                        key: 'name-mappings',
                        label: 'Name Mappings',
                        children: <NameMappingsTable />,
                    },
                ]}
            />
        </div>
    );
};

export default Employees;

